import styled from "@xstyled/styled-components";
import { motion } from "framer-motion";

import { ApplicationProcess } from "../components/ApplicationProcess";
import { CompanyBenefits } from "../components/CompanyBenefits";
import { EmployeeEndorsements } from "../components/EmployeeEndorsements";
import { JobInvolves } from "../components/JobInvolves";
import { JobRequirements } from "../components/JobRequirements";
import { SalarySubmission } from "../components/SalarySubmission";
import { VisaDisclaimer } from "../components/VisaDisclaimer";
import {
  CompanyOtherJobsButton,
  CompanyOtherJobsSection,
} from "../components/CompanyOtherJobs";
import { CompanyLocation } from "../components/CompanyLocation";
import { HideCompanyLink } from "../components/HideCompanyLink";
import { JobCardSection } from "../components/JobCardSection";
import { ReportAProblem } from "../components/ReportAProblem";
import { ShareLink } from "../components/ShareLink";
import SalaryBenchmarks from "../components/SalaryBenchmarks";
import { MeetTheTeamSection } from "../components/MeetTheTeamSection";

import { SeparateContent } from "./DesktopContent";

import { CompanyFunding } from "@otta/search/components/CompanyFunding";
import { OttasTake } from "@otta/search/components/OttasTake";
import { Insights } from "@otta/search/components/Insights";
import { CompanyAdditionalInfo } from "@otta/search/components/CompanyAdditionalInfo";
import { CompanyValues } from "@otta/search/components/CompanyValues";
import { CompanyArticles } from "@otta/search/components/CompanyArticles";
import { PeopleAtCompany } from "@otta/search/components/PeopleAtCompany";
import { palette, pxToRem } from "@otta/design-tokens";
import { Logo } from "@otta/design";
import { Job, UserJobPreferences } from "@otta/search/schema";
import { DiversityInitiatives } from "@otta/search/components/DiversityInitiatives";

const OttaLogo = styled(Logo)`
  height: ${pxToRem(20)};
`;

const MainContentContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(24)};
  max-width: 1024px;
`;

const ActionButtonContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${pxToRem(16)};
`;

const CompanyContainer = styled.div`
  max-width: 100vw;
`;

interface DesktopContentProps {
  job: Job.Fragment;
  user?: UserJobPreferences.CurrentUser;
  otherJobsExpanded: boolean;
  expandOtherJobs?: () => void;
}

export function MobileContent({
  job,
  user,
  otherJobsExpanded,
  expandOtherJobs,
}: DesktopContentProps): React.ReactElement {
  return (
    <MainContentContainer data-testid="job-card-main">
      <RoleInfoSection
        job={job}
        user={user}
        otherJobsExpanded={otherJobsExpanded}
        expandOtherJobs={expandOtherJobs}
      />
      <OttasTakeSection job={job} />
      <CompanyInfoSection job={job} user={user} />
      <ActionButtonsSection
        job={job}
        user={user}
        otherJobsExpanded={otherJobsExpanded}
        expandOtherJobs={expandOtherJobs}
      />
    </MainContentContainer>
  );
}

function RoleInfoSection({
  job,
  user,
}: Pick<
  DesktopContentProps,
  "job" | "user" | "expandOtherJobs" | "otherJobsExpanded"
>): React.ReactElement {
  return (
    <JobCardSection title="Role">
      <SeparateContent>
        <JobRequirements job={job} />
        <JobInvolves job={job} />
        <ApplicationProcess job={job} />
        <SalaryBenchmarks job={job} user={user} />
      </SeparateContent>
    </JobCardSection>
  );
}

function CompanyInfoSection({
  job,
  user,
}: Pick<DesktopContentProps, "job" | "user">): React.ReactElement {
  return (
    <CompanyContainer>
      <JobCardSection title="Insights">
        <SeparateContent>
          <Insights job={job} company={job.company} />
        </SeparateContent>
      </JobCardSection>
      {!!job.team && (
        <MeetTheTeamSection
          team={job.team}
          members={job.teamMembers}
          jobTitle={job.title}
          jobId={job.externalId}
        />
      )}
      <JobCardSection title="Company">
        <SeparateContent>
          <EmployeeEndorsements company={job.company} user={user} />
          <CompanyFunding company={job.company} />
          <CompanyBenefits job={job} company={job.company} />
          <CompanyValues company={job.company} />
          <CompanyLocation job={job} company={job.company} />
          <VisaDisclaimer job={job} company={job.company} user={user} />
          <CompanyArticles company={job.company} />
          <CompanyAdditionalInfo company={job.company} />
          <PeopleAtCompany company={job.company} showBorder />
          <SalarySubmission job={job} user={user} />
          <DiversityInitiatives company={job.company} />
        </SeparateContent>
      </JobCardSection>
    </CompanyContainer>
  );
}

function OttasTakeSection({
  job,
}: Pick<DesktopContentProps, "job">): React.ReactElement {
  return (
    <JobCardSection
      title={job.company.name === "Otta" ? "Our take" : "Otta's take"}
      titleColor={palette.brand.yellow}
      tooltipDescription="This is Otta's opinion, written to help you understand more about the company. It's not intended as a statement of fact, but was written to be accurate as possible at the time of publishing."
      TopRightComponent={<OttaLogo />}
    >
      <OttasTake company={job.company} />
    </JobCardSection>
  );
}

function ActionButtonsSection({
  job,
  user,
  otherJobsExpanded,
  expandOtherJobs,
}: Pick<
  DesktopContentProps,
  "job" | "user" | "otherJobsExpanded" | "expandOtherJobs"
>): React.ReactElement {
  return (
    <>
      <JobCardSection>
        <ActionButtonContainer>
          <ShareLink job={job} />
          <ReportAProblem job={job} user={user} />
          <HideCompanyLink company={job.company} user={user} />
          <CompanyOtherJobsButton
            company={job.company}
            user={user}
            currentJobId={job.id}
            expanded={otherJobsExpanded}
            expand={expandOtherJobs}
          />
        </ActionButtonContainer>
      </JobCardSection>

      {otherJobsExpanded && job.company.liveJobs.length > 1 && (
        <motion.div
          key="job-card-company-jobs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ opacity: { ease: "easeInOut" } }}
          style={{ width: "100%" }}
        >
          <JobCardSection>
            <CompanyOtherJobsSection
              company={job.company}
              user={user}
              currentJobId={job.id}
            />
          </JobCardSection>
        </motion.div>
      )}
    </>
  );
}
